/**
 * 현장 담당자 관리 섹션 — 현장 설정 페이지 하위 컴포넌트.
 *
 * UX:
 *   - 테이블: 이름 / 회사 / 휴대폰 / 담당내용 / 액션(수정/삭제)
 *   - 우상단: "담당자 추가" 버튼 → 다이얼로그
 *   - admin/editor 만 쓰기 가능 (백엔드도 동일 권한 가드)
 *
 * 4 필드만 — user 결정. user_id 연결은 미도입.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PeopleAlt as PeopleIcon,
} from '@mui/icons-material';
import {
  CreateSiteContactRequest,
  SiteContact,
  SiteContactService,
} from '../../services/SiteContactService';
import { getUserInfo } from '../../utils/auth';

interface Props {
  projectId: number;
}

interface FormState {
  name: string;
  company: string;
  role: string;
  phone: string;
  email: string;
  responsibility: string; // UI label: "비고"
}

const EMPTY_FORM: FormState = {
  name: '',
  company: '',
  role: '',
  phone: '',
  email: '',
  responsibility: '',
};

function canEdit(): boolean {
  const u = getUserInfo();
  return u?.role === 'admin' || u?.role === 'editor';
}

const ContactsSection: React.FC<Props> = ({ projectId }) => {
  const [items, setItems] = useState<SiteContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const writable = useMemo(() => canEdit(), []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await SiteContactService.list(projectId);
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : '담당자 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (c: SiteContact) => {
    setEditingId(c.id);
    setForm({
      name: c.name,
      company: c.company ?? '',
      role: c.role ?? '',
      phone: c.phone ?? '',
      email: c.email ?? '',
      responsibility: c.responsibility ?? '',
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    const name = form.name.trim();
    if (!name) {
      setFormError('담당자 이름이 필요합니다.');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const payload: CreateSiteContactRequest = {
        name,
        company: form.company.trim() || undefined,
        role: form.role.trim() || undefined,
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        responsibility: form.responsibility.trim() || undefined,
      };
      if (editingId == null) {
        await SiteContactService.create(projectId, payload);
      } else {
        await SiteContactService.update(editingId, payload);
      }
      setDialogOpen(false);
      await refresh();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : '저장 실패');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (c: SiteContact) => {
    if (!window.confirm(`'${c.name}' 담당자를 삭제하시겠습니까?`)) return;
    try {
      await SiteContactService.delete(c.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : '삭제 실패');
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mt: 3 }} id="contacts">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <PeopleIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>
            현장 담당자
          </Typography>
          <Typography variant="caption" color="text.secondary">
            현장 운영에 관여하는 사람 정보 — 개요 화면 상단에 노출됩니다.
          </Typography>
        </Stack>
        {writable && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ textTransform: 'none' }}
          >
            담당자 추가
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : items.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          등록된 담당자가 없습니다.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>이름</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>회사</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>직책</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>휴대폰</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>이메일</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>비고</TableCell>
                {writable && <TableCell align="right" sx={{ width: 100 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{c.name}</TableCell>
                  <TableCell>{c.company || <em>—</em>}</TableCell>
                  <TableCell>{c.role || <em>—</em>}</TableCell>
                  <TableCell>
                    {c.phone ? (
                      <a
                        href={`tel:${c.phone.replace(/\s+/g, '')}`}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                      >
                        {c.phone}
                      </a>
                    ) : (
                      <em>—</em>
                    )}
                  </TableCell>
                  <TableCell>
                    {c.email ? (
                      <a
                        href={`mailto:${c.email}`}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                      >
                        {c.email}
                      </a>
                    ) : (
                      <em>—</em>
                    )}
                  </TableCell>
                  <TableCell>{c.responsibility || <em>—</em>}</TableCell>
                  {writable && (
                    <TableCell align="right">
                      <Tooltip title="수정">
                        <IconButton size="small" onClick={() => openEdit(c)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="삭제">
                        <IconButton size="small" onClick={() => handleDelete(c)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => !submitting && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingId == null ? '담당자 추가' : '담당자 수정'}</DialogTitle>
        <DialogContent dividers>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Stack spacing={2}>
            <TextField
              label="이름"
              required
              fullWidth
              autoFocus
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="예: 김운영"
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="회사"
                fullWidth
                value={form.company}
                onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                placeholder="예: 럭스로보 / 시공 협력사"
              />
              <TextField
                label="직책"
                fullWidth
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                placeholder="예: 관리소장 / PM"
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="휴대폰번호"
                fullWidth
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="예: 010-1234-5678"
              />
              <TextField
                label="이메일주소"
                type="email"
                fullWidth
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="name@example.com"
              />
            </Stack>
            <TextField
              label="비고"
              fullWidth
              multiline
              minRows={2}
              value={form.responsibility}
              onChange={(e) => setForm((f) => ({ ...f, responsibility: e.target.value }))}
              placeholder="근무시간 / 대체 연락처 / 담당 범위 등"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting}>
            취소
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? '저장 중...' : editingId == null ? '추가' : '수정'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ContactsSection;
